import { React, useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { AiFillSchedule } from "react-icons/ai";
import * as XLSX from "xlsx";
import {
  Button,
  Select,
  Option,
  Dialog,
  DialogHeader,
  DialogBody,
  Checkbox,
  List,
  ListItem,
  Typography,
} from "@material-tailwind/react";

import { colorsConfig } from "../../config/colorsConfig";
import { useContactsContext } from "../../contexts/ContactsContextProvider";
import { useWhatsappContext } from "../../contexts/WhatsappContextProvider";
import { Loader } from "..";
import { isoTimestampToLocal } from "../utils/timeStampConversion";
import { filterWaMsgStatus } from "../../data/filterData.js";

const ViewTrackingContactsTable = ({ scheduleStatus, jobId, selectedJob }) => {
  const {
    scheduledCampaignsTrackingData,
    isCampaignDataLoading,
    contactGroups,
    apiCallAssignContactGroups,
    apiCallFetchContactGroups,
    syncCampaignMessageStatus,
  } = useContactsContext();
  const { resendSelectMessages, isProcessing } = useWhatsappContext();

  const [isVisibleSendButton, setIsVisibleSendButton] = useState(false);
  const [selectedContact, setSelectedContact] = useState([]);
  const [selectedContactToAddGroup, setSelectedContactToAddGroup] = useState(
    []
  );
  const [targetContactGroup, setTargetContactGroup] = useState("");
  const [openGroupCreationDialog, setOpenGroupCreationDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isMessageSync, setIsMessageSync] = useState(false);
  const [filteredData, setFilteredData] = useState(
    scheduledCampaignsTrackingData
  );

  const handleCheckboxChange = (event, item) => {
    const { checked } = event.target;

    setSelectedItems((prevSelectedItems) =>
      checked
        ? [...prevSelectedItems, item]
        : prevSelectedItems.filter(
            (selectedItem) => selectedItem.name !== item.name
          )
    );
  };

  const assignContactGroupsOnSubmit = async (event) => {
    event.preventDefault();

    if (contactGroups.filter((x) => x.groupName === targetContactGroup)) {
      const result = await apiCallAssignContactGroups(
        selectedContactToAddGroup,
        targetContactGroup
      );

      if (result?.success) {
        setOpenGroupCreationDialog(false);
        setIsVisibleSendButton(false);
      }
    }
  };

  let selectedFailedContact = [];
  let selectedContactToGroup = [];
  const conditionalRowStyles = [
    {
      when: (row) => row?.MessageStatusFromWhatsapp === "read",
      style: {
        color: "green",
      },
    },
    {
      when: (row) => row?.MessageStatusFromWhatsapp === "delivered",
      style: {
        color: "blue",
      },
    },
    {
      when: (row) => row?.MessageStatusFromWhatsapp === "failed",
      style: {
        color: "red",
      },
    },
  ];

  const scheduleTrackingTableColumns = [
    {
      name: "CC",
      selector: (row) => row?.countryCode,
      minWidth: "50px",
    },
    {
      name: "Phone Number",
      selector: (row) => row?.phoneNumber,
      minWidth: "180px",
    },
    {
      name: "Contact Name",
      selector: (row) => (row?.firstName || "") + " " + (row?.lastName || ""),
      sortable: true,
      minWidth: "250px",
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      center: true,
      minWidth: "100px",
    },
    {
      name: "Company",
      selector: (row) => row?.companyName,
      minWidth: "200px",
    },
    {
      name: "Send Status",
      selector: (row) => row?.MessageStatus,
      center: true,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Message Status",
      selector: (row) => row?.MessageStatusFromWhatsapp,
      center: true,
      sortable: true,
      minWidth: "150px",
      conditionalCellStyles: conditionalRowStyles,
    },
    {
      name: "Code",
      selector: (row) => row?.failed_reason_error_code,
      center: true,
      minWidth: "150px",
    },
    {
      name: "Remarks",
      selector: (row) => row?.failed_reason,
      center: false,
      wrap: true,
      minWidth: "250px",
    },
  ];

  const handleChangeOnSelect = ({ selectedRows }) => {
    selectedFailedContact = [];
    selectedContactToGroup = [];
    selectedRows?.map((x) =>
      selectedFailedContact.push({
        phoneNo: x?.phoneNumber,
        countryCode: x?.countryCode,
      })
    );
    selectedRows?.map((x) =>
      selectedContactToGroup.push(
        JSON.stringify({
          phoneNo: x?.phone_number_c,
          countryCode: x?.country_code_si,
        })
      )
    );

    if (selectedFailedContact.length > 0) {
      setSelectedContact(selectedFailedContact);
      setSelectedContactToAddGroup(selectedContactToGroup);
      setIsVisibleSendButton(true);
    } else {
      setSelectedContact([]);
      setIsVisibleSendButton(false);
    }
  };

  const handleResendOnClick = async (e) => {
    e.preventDefault();
    await resendSelectMessages({
      scheduleId: jobId,
      selectedContacts: selectedContact,
      scheduleStatus: scheduleStatus,
    });
  };

  const handleCampaignStatusSyncOnClick = async (e) => {
    setIsMessageSync(true);
    const response = await syncCampaignMessageStatus(jobId);
    if (response?.success) {
      toast.success(response.message);
      setIsMessageSync(false);
    } else {
      toast.error(response.message);
      setIsMessageSync(false);
    }
  };

  const downloadSelectedContactsOnClick = async () => {
    let contactArray = [];
    // eslint-disable-next-line array-callback-return
    filteredData?.map((contact) => {
      contactArray.push({
        first_name: contact?.firstName,
        last_name: contact?.lastName,
        country_code: contact?.countryCode,
        phone_number: contact?.phoneNumber,
        email: contact?.email,
        company_name: contact?.companyName,
        city: contact?.city,
        "Send Status": contact?.MessageStatus,
        "Message Status": contact?.MessageStatusFromWhatsapp,
      });
    });

    const dataToDownload = contactArray;
    const worksheet = XLSX.utils.json_to_sheet(dataToDownload);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `Campaign data - ${selectedJob?.scheduleName} - ${isoTimestampToLocal(
        selectedJob?.scheduleDateTime
      )}.xlsx`
    );
  };

  useEffect(() => {
    if (selectedItems.length === 0) {
      setFilteredData(scheduledCampaignsTrackingData);
    } else {
      const trackingFilteredData = scheduledCampaignsTrackingData.filter(
        (dataItem) =>
          selectedItems.some(
            (selectedItem) =>
              selectedItem.name === dataItem.MessageStatusFromWhatsapp
          )
      );
      setFilteredData(trackingFilteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  useEffect(() => {
    apiCallFetchContactGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredData(scheduledCampaignsTrackingData);
  }, [scheduledCampaignsTrackingData]);

  return (
    <main className="p-5 space-y-2">
      <h3 className="wabm-font-style-2 text-white">Message Tracking Status</h3>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <div className="col-span-2 space-y-3 text-white text-sm">
          <span className="flex items-center gap-2 text-white">
            <AiFillSchedule size={24} />
            <h4 className="text-xl font-bold">{selectedJob?.scheduleName}</h4>
          </span>
          <p className="italic">{selectedJob?.scheduleName}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3">
            <p>
              Created : {isoTimestampToLocal(selectedJob?.scheduleCreationDate)}
            </p>
            <p>
              Scheduled : {isoTimestampToLocal(selectedJob?.scheduleDateTime)}
            </p>
            <p>Status : {selectedJob?.scheduleStatus}</p>
            <p className="w-80 break-words whitespace-normal">
              Template : {selectedJob?.TemplateName}
            </p>
            <p>Type : {selectedJob?.contactType}</p>
            <p>Info : {selectedJob?.contactInfo}</p>
            <p>Total : {selectedJob?.TotalContactCount}</p>
            <p>Delivered : {selectedJob?.deliveredCount}</p>
          </div>
        </div>

        <div className="wbm-card-3 text-white">
          <p>Filter the contacts with their message status</p>
          <p>Filtered Contacts Count : {filteredData && filteredData.length}</p>
          <List className="flex-row">
            <ListItem className="p-1">
              {filterWaMsgStatus.map((fld) => (
                <Checkbox
                  ripple={false}
                  className="m-2 hover:before:opacity-0 text-green-600"
                  color={fld?.color}
                  label={
                    <Typography
                      color={fld?.color}
                      className="text-lg font-medium"
                    >
                      {fld?.text}
                    </Typography>
                  }
                  containerProps={{
                    className: "p-0",
                  }}
                  checked={selectedItems.some(
                    (selectedItem) => selectedItem?.name === fld?.name
                  )}
                  onChange={(e) => handleCheckboxChange(e, fld)}
                />
              ))}
            </ListItem>
          </List>
          <div className="flex justify-center gap-2">
            <Button
              color="green"
              size="sm"
              disabled={isMessageSync}
              onClick={() => handleCampaignStatusSyncOnClick()}
              className="w-fit px-5 whitespace-nowrap"
            >
              <span>Sync Message Status</span>
            </Button>
            <Button
              color="green"
              size="sm"
              onClick={() => downloadSelectedContactsOnClick()}
              className="w-fit px-5 whitespace-nowrap"
            >
              <span>Download Report</span>
            </Button>
            {isVisibleSendButton && (
              <>
                <Button
                  color="yellow"
                  size="sm"
                  disabled={isProcessing}
                  onClick={handleResendOnClick}
                  className="w-fit px-5 whitespace-nowrap"
                >
                  {isProcessing ? <span>Sending...</span> : <span>Resend</span>}
                </Button>
                <Button
                  color="blue"
                  size="sm"
                  onClick={() => {
                    setOpenGroupCreationDialog(true);
                  }}
                  className="w-fit px-5 whitespace-nowrap"
                >
                  <span>Add to contact group</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="w-full pt-3 rounded-md overflow-auto">
        {isCampaignDataLoading ? (
          <Loader />
        ) : (
          <DataTable
            fixedHeader
            selectableRows
            onSelectedRowsChange={handleChangeOnSelect}
            columns={scheduleTrackingTableColumns}
            data={filteredData}
            fixedHeaderScrollHeight="67vh"
            customStyles={scheduleTableStyles}
          />
        )}
      </div>

      <Dialog
        open={openGroupCreationDialog}
        handler={() => setOpenGroupCreationDialog(!openGroupCreationDialog)}
      >
        <span className="flex justify-between gap-3">
          <DialogHeader>Add Contact Group</DialogHeader>
        </span>
        <DialogBody>
          <form
            onSubmit={assignContactGroupsOnSubmit}
            className="flex flex-row md:flex-nowrap items-center gap-3"
          >
            <Select
              variant="outlined"
              label="Select Contact Group"
              color="blue"
              required
              value={targetContactGroup}
              onChange={(e) => setTargetContactGroup(e)}
              className="w-full"
            >
              {contactGroups?.map((group, index) => (
                <Option key={index} value={group?.groupName}>
                  {group?.groupName} (
                  {group?.numberOfContacts ? group.numberOfContacts : 0}{" "}
                  contacts)
                </Option>
              ))}
            </Select>
            <div className="flex flex-row items-center gap-2">
              <Button type="submit" variant="gradient" size="sm" color="green">
                <span className="whitespace-nowrap">Add to Group</span>
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </main>
  );
};

export default ViewTrackingContactsTable;

const scheduleTableStyles = {
  rows: {
    style: {},
  },
  headCells: {
    style: {
      backgroundColor: colorsConfig.wabmBlue,
      color: "#FFFFFF",
      fontSize: "0.9rem",
    },
  },
  cells: {
    style: {},
  },
};
